import { useState, useEffect } from 'react';

/**
 * Tracks the virtual keyboard height on mobile using the Visual Viewport API.
 * Returns the keyboard offset in pixels (0 when keyboard is closed).
 *
 * How it works:
 *   - window.innerHeight = full layout viewport (never changes when keyboard opens)
 *   - window.visualViewport.height = the visible area (shrinks when keyboard opens)
 *   - keyboard height = innerHeight - visualViewport.height - visualViewport.offsetTop
 *
 * Supported: iOS Safari 13+, Android Chrome 61+, all modern browsers.
 */
export function useKeyboardOffset(): number {
  const [keyboardOffset, setKeyboardOffset] = useState(0);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      // offsetTop accounts for iOS viewport scrolling above the fold
      const offset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardOffset(offset);
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return keyboardOffset;
}
